// Opens a loan link in a genuinely separate browser window rather than a tab.
//
// target="_blank" cannot do this. Every modern browser reads _blank as "wherever
// the user prefers", and the default preference is a tab — so the Polly and
// Encompass links kept landing in tabs no matter how they were marked up. A
// window only appears when window.open is given a features string: that is the
// signal browsers use to tell "new window" from "new tab", and Safari is the
// strictest about it.
//
// Two constraints come with that. The call has to run inside the click handler's
// own call stack, because anything async loses the user-gesture attribution and
// Safari's pop-up blocker takes it. And 'noopener' must stay out of the features
// string: with it, window.open returns null by spec, which would make a real
// window indistinguishable from a blocked one. The opener is cleared by hand
// below instead.

const MAX_WIDTH = 1400
const MAX_HEIGHT = 900
const MIN_WIDTH = 900
const MIN_HEIGHT = 650

function buildWindowFeatures() {
  const screenWidth = window.screen?.availWidth || window.innerWidth || MAX_WIDTH
  const screenHeight = window.screen?.availHeight || window.innerHeight || MAX_HEIGHT

  const width = Math.max(MIN_WIDTH, Math.min(MAX_WIDTH, Math.round(screenWidth * 0.85)))
  const height = Math.max(MIN_HEIGHT, Math.min(MAX_HEIGHT, Math.round(screenHeight * 0.9)))

  // Offset from this window's own position so a second monitor keeps the loan
  // on the screen the user is already working on.
  const left = Math.round((window.screenX || 0) + Math.max(0, (screenWidth - width) / 2))
  const top = Math.round((window.screenY || 0) + Math.max(0, (screenHeight - height) / 2))

  return `popup=yes,width=${width},height=${height},left=${left},top=${top}`
}

// Returns true only when a separate window actually opened.
//
// Callers rendering an <a target="_blank"> should preventDefault on true and do
// nothing on false — a blocked pop-up then falls through to the link's own
// navigation, so the loan still opens. Callers rendering a <button> have no such
// fallback and should open a plain tab when this returns false.
export function openLoanInWindow(url, event) {
  if (!url) return false

  // cmd/ctrl/shift-click and middle-click mean the user has already said where
  // they want it. Leave those to the browser.
  if (event && (
    event.metaKey ||
    event.ctrlKey ||
    event.shiftKey ||
    event.altKey ||
    (typeof event.button === 'number' && event.button !== 0)
  )) {
    return false
  }

  let opened = null

  try {
    opened = window.open(url, '_blank', buildWindowFeatures())
  } catch (e) {
    opened = null
  }

  if (!opened) return false

  // Reverse-tabnabbing guard, done by hand because 'noopener' in the features
  // string would have cost us the handle we just checked.
  try { opened.opener = null } catch (e) { /* cross-origin already denies it */ }
  try { opened.focus() } catch (e) { /* focus is a courtesy, not a requirement */ }

  return true
}

export default { openLoanInWindow }
